import { defineMessages, useIntl } from 'react-intl'
import useViewport from '../../../../hooks/useViewport'
import {
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  Heading,
  Icon,
  Tab,
  TabList,
  Tabs,
  Text
} from '@homebotapp/hb-react-component-catalog'
import { ListingMarketReport, MarketReport } from '../../../../api/gqlaxy/gql/generated/graphql'
import { formatAbbrCurrency, formatNumber } from '../../../../helpers/formatters'
import Section from '../Section/Section'
import { useState } from 'react'
import { PATH_HELPERS } from '../../../../constants/navigation'
import history from '../../../../history'
import { useDispatch, useSelector } from 'react-redux'
import { selectClientId, selectClientIsGuest } from '../../../../store/selectors/client'
import { HomeSearchTrackingEventLocation } from '../../../../constants/tracking'
import { toggleAuthModal } from '../../../../actions/homeSearchAuthModal'
import {
  StatBox,
  TemperatureIndicator,
  MarketLabels,
  MARKET_TEMPERATURE_CONSTANTS
} from '../../../../Components/shared/MarketTemperature'

const { BUYER_MARKET_THRESHOLD, BALANCED_MARKET_THRESHOLD, COVERAGE_SCORE_THRESHOLD } = MARKET_TEMPERATURE_CONSTANTS

interface MarketTemperatureCardProps {
  marketData: ListingMarketReport
  listingPrice: number
}

export const MSG = defineMessages({
  howIsTheMarket: {
    id: 'MarketTemperatureCard.howIsTheMarket',
    defaultMessage: "How's the market in {zipCode}?"
  },
  homesAroundPrice: {
    id: 'MarketTemperatureCard.homesAroundPrice',
    defaultMessage: 'Homes around {price}'
  },
  allHomes: {
    id: 'MarketTemperatureCard.allHomes',
    defaultMessage: 'All homes'
  },
  homesForSale: {
    id: 'MarketTemperatureCard.homesForSale',
    defaultMessage: 'Homes for sale'
  },
  averageSize: {
    id: 'MarketTemperatureCard.averageSize',
    defaultMessage: 'Average size'
  },
  averageTimeToSell: {
    id: 'MarketTemperatureCard.averageTimeToSell',
    defaultMessage: 'Average time to sell'
  },
  days: {
    id: 'MarketTemperatureCard.days',
    defaultMessage: '{count, plural, one {# day} other {# days}}'
  },
  infoCardHeaderBuyerMarket: {
    id: 'MarketTemperatureCard.InfoCardHeaderBuyerMarket',
    defaultMessage: "Buying in a Buyer's Market"
  },
  infoCardBodyBuyerMarket: {
    id: 'MarketTemperatureCard.InfoCardBodyBuyerMarket',
    defaultMessage:
      "You may have some wiggle room to negotiate concessions and lower prices. There likely won't be as much competition or time pressure"
  },
  infoCardHeaderSellerMarket: {
    id: 'MarketTemperatureCard.InfoCardHeaderSellerMarket',
    defaultMessage: "Buying in a Seller's Market"
  },
  infoCardBodySellerMarket: {
    id: 'MarketTemperatureCard.InfoCardBodySellerMarket',
    defaultMessage:
      'Consider getting pre-approved early, and be prepared to make quick decisions and competitive offers.'
  },
  infoCardHeaderBalancedMarket: {
    id: 'MarketTemperatureCard.InfoCardHeaderBalancedMarket',
    defaultMessage: 'Buying in a Balanced Market'
  },
  infoCardBodyBalancedMarket: {
    id: 'MarketTemperatureCard.InfoCardBodyBalancedMarket',
    defaultMessage: 'Be prepared for reasonable negotiations and standard timelines.'
  },
  moreMarketInfo: {
    id: 'MarketTemperatureCard.moreMarketInfoBtn',
    defaultMessage: 'More market info'
  },
  emptyState: {
    id: 'MarketTemperatureCard.emptyState',
    defaultMessage: "Sorry, we don't have enough market data for this area"
  }
})

export const MarketTemperatureCard: React.FC<MarketTemperatureCardProps> = ({ marketData, listingPrice }) => {
  const viewport = useViewport()

  const intl = useIntl()
  const dispatch = useDispatch()
  const { isSmall } = useViewport()

  const clientId = useSelector(selectClientId)
  const isGuest = useSelector(selectClientIsGuest)

  const [selectedMarket, setSelectedMarket] = useState<MarketReport>(marketData.pricedAroundTab)

  return (
    <Section>
      <Section.Body>
        <Flex direction='column' justifyContent='center'>
          <Text size='lg' fontWeight='bold' color='neutral.900' textAlign='center' mb={0}>
            {intl.formatMessage(MSG.howIsTheMarket, {
              zipCode: marketData.zipcode
            })}
          </Text>
          <Tabs size='sm' orientation='horizontal' my={2}>
            <TabList justifyContent='center' borderBottom='2px solid' borderColor='neutral.400'>
              <Tab
                onClick={() => setSelectedMarket(marketData.pricedAroundTab)}
                tracking={{
                  guid: 'uvmc9VP9kvNqFaMT7Pch8S',
                  descriptor: 'Homes around {price}',
                  ui_context:
                    HomeSearchTrackingEventLocation.HOME_SEARCH_LISTING_DETAILS_MARKET_TEMPERATURE_PRICE_AROUND_TAB
                }}
              >
                {intl.formatMessage(MSG.homesAroundPrice, { price: formatAbbrCurrency(listingPrice) })}
              </Tab>
              <Tab
                onClick={() => setSelectedMarket(marketData.allHomesTab)}
                tracking={{
                  guid: '6wGKf9uetvKqmn9K4jeVPN',
                  descriptor: 'Homes around {price}',
                  ui_context:
                    HomeSearchTrackingEventLocation.HOME_SEARCH_LISTING_DETAILS_MARKET_TEMPERATURE_ALL_HOMES_TAB
                }}
              >
                {intl.formatMessage(MSG.allHomes)}
              </Tab>
            </TabList>
          </Tabs>

          {selectedMarket?.coverageScore > COVERAGE_SCORE_THRESHOLD ? (
            <>
              <TemperatureIndicator marketTemp={selectedMarket?.marketTemp} isSmall={isSmall} />
              <MarketLabels />
              <Flex
                justifyContent='center'
                gap={viewport.isSmall ? '12px' : 10}
                flexDirection={viewport.isSmall ? 'column' : 'row'}
              >
                {selectedMarket?.inventoryTotal > 0 && (
                  <StatBox
                    icon='🪧'
                    label={intl.formatMessage(MSG.homesForSale)}
                    value={selectedMarket?.inventoryTotal.toString()}
                  />
                )}
                {selectedMarket?.averageSqft && (
                  <StatBox
                    icon='🏠'
                    label={intl.formatMessage(MSG.averageSize)}
                    value={`${formatNumber(selectedMarket?.averageSqft)} SQFT`}
                  />
                )}
                {selectedMarket?.daysOnMarket && (
                  <StatBox
                    icon='🚛'
                    label={intl.formatMessage(MSG.averageTimeToSell)}
                    value={intl.formatMessage(MSG.days, { count: selectedMarket?.daysOnMarket })}
                  />
                )}
              </Flex>
              <Flex justifyContent='center'>
                <Card
                  mt={4}
                  // @ts-ignore
                  backgroundColor='var(--hb-colors-primary-50) !important'
                  width={viewport.isSmall ? '100%' : '65%'}
                >
                  <CardBody p={3} display='flex' gap={3}>
                    <Icon mt={1} color='primary.700' name='info-circle-fa' />
                    <Box>
                      <Heading mb={0} as='h5' size='2xs' textAlign='left'>
                        {selectedMarket?.marketTemp <= BUYER_MARKET_THRESHOLD
                          ? intl.formatMessage(MSG.infoCardHeaderBuyerMarket)
                          : selectedMarket?.marketTemp <= BALANCED_MARKET_THRESHOLD
                          ? intl.formatMessage(MSG.infoCardHeaderBalancedMarket)
                          : intl.formatMessage(MSG.infoCardHeaderSellerMarket)}
                      </Heading>
                      <Text size='xs' mb={0}>
                        {selectedMarket?.marketTemp <= BUYER_MARKET_THRESHOLD
                          ? intl.formatMessage(MSG.infoCardBodyBuyerMarket)
                          : selectedMarket?.marketTemp <= BALANCED_MARKET_THRESHOLD
                          ? intl.formatMessage(MSG.infoCardBodyBalancedMarket)
                          : intl.formatMessage(MSG.infoCardBodySellerMarket)}
                      </Text>
                    </Box>
                  </CardBody>
                </Card>
              </Flex>
              <Button
                rightIcon={<Icon name='circle-arrow-right-bts' />}
                colorScheme='primary'
                variant='ghost'
                mt={4}
                tracking={{
                  guid: 'ubuGzzBtQm4E5pz4kLxUaF',
                  descriptor: MSG.moreMarketInfo.defaultMessage,
                  ui_context:
                    HomeSearchTrackingEventLocation.HOME_SEARCH_LISTING_DETAILS_MARKET_TEMPERATURE_MORE_MARKET_INFO
                }}
                onClick={() => {
                  isGuest || !clientId
                    ? dispatch(toggleAuthModal(true))
                    : history.push(PATH_HELPERS.zip.buildPath(clientId, marketData.zipcode))
                }}
              >
                {intl.formatMessage(MSG.moreMarketInfo)}
              </Button>
            </>
          ) : (
            <Text my={8} size='lg' as='b' color='neutral-900' textAlign='center'>
              {intl.formatMessage(MSG.emptyState)}
            </Text>
          )}
        </Flex>
      </Section.Body>
    </Section>
  )
}
